import React, { useEffect, useState } from 'react';
import styles from './access.module.css';
import { fontSize, palette } from '../..';
import { FormControl, MenuItem, Select, Tooltip } from '@mui/material';
import { KeyedMutator } from 'swr';
import StepSlider from './Slider';
import { TAlert } from '../../types/types';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import FetchData from '../ServerCall/FetchData';
import { useParams } from 'react-router-dom';

type UserInRow = {
  id: string;
  name: string;
  isGroup: boolean;
  emailId: string;
  role: 'owner' | 'moderator' | 'creator' | 'custom_creator' | 'player';
  customPermissions?: [
    {
      contentTypeId: number;
      privilegeId: number;
    },
  ];
};

const AccessTable = ({
  accessList,

  setUser_GroupName,
  setIsInGroup,
  currentRoleID,
  disableOwnerAccess,
  disableModeratorAccess,
  customPermissions,
  rowInEditMode,
  originalAccessList,

  mutateAccessList,
  setAddedUser,
  setShowUserDeleteConfirmation,
  setUser_GroupID,
  setCustomPermissions,
  setRowInEditMode,
  setOriginalAccessList,
  addedUser,
}: {
  accessList: any[];
  addedUser: UserInRow | null;
  currentRoleID: number;
  setUser_GroupName: any;
  setIsInGroup: any;
  disableModeratorAccess: boolean;
  disableOwnerAccess: boolean;
  customPermissions: any;
  rowInEditMode: UserInRow | null;
  originalAccessList: any[];

  setShowUserDeleteConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  setUser_GroupID: React.Dispatch<React.SetStateAction<string>>;
  setCustomPermissions: React.Dispatch<React.SetStateAction<any>>;
  setRowInEditMode: React.Dispatch<React.SetStateAction<UserInRow | null>>;
  setOriginalAccessList: React.Dispatch<React.SetStateAction<any[]>>;
  setAddedUser: React.Dispatch<React.SetStateAction<UserInRow | null>>;

  mutateAccessList: KeyedMutator<any>;
}) => {
  const { parentId } = useParams();

  const [hoverRowState, setHoverRowState] = useState<number | null>(null);

  const roles = [
    { id: 3, value: 'owner', displayValue: 'Owner' },
    { id: 4, value: 'moderator', displayValue: 'Moderator' },
    { id: 5, value: 'creator', displayValue: 'Creator' },
    { id: 6, value: 'custom_creator', displayValue: 'Custom Creator' },
    { id: 7, value: 'player', displayValue: 'Player' },
  ];

  const [alert, setAlert] = useState<null | TAlert>(null);

  useEffect(() => {
    if (alert) {
      setTimeout(() => {
        setAlert(null);
      }, 3000);
    }
  }, [alert]);

  const sortAccessList = (accessList: any[]) => {
    return accessList.sort((a, b) => {
      if (a.id === addedUser?.id) return -1;
      if (b.id === addedUser?.id) return 1;
      return a.name.localeCompare(b.name);
    });
  };

  const handleSaveButtonClick = async () => {
    if (!rowInEditMode) {
      setAlert({
        message: 'No row is being edited.',
        severity: 'error',
      });
      return;
    }

    // TODO: Ask Chirag in what case we will have roleName here?
    const roleId = roles.find((roles) => roles.value === rowInEditMode?.role)?.id;

    let payload = [];

    // Set custom permissions if the role is custom_creator
    if (roleId === 6) {
      payload = [
        {
          granteeId: rowInEditMode?.id,
          isGroup: rowInEditMode?.isGroup,
          roleId: roleId,
          customPermissions: [
            {
              contentTypeId: 2,
              privilegeId: customPermissions[rowInEditMode?.id]?.[2] || null,
            },
            {
              contentTypeId: 3,
              privilegeId: customPermissions[rowInEditMode?.id]?.[3] || null,
            },
            {
              contentTypeId: 4,
              privilegeId: customPermissions[rowInEditMode?.id]?.[4] || null,
            },
            {
              contentTypeId: 5,
              privilegeId: customPermissions[rowInEditMode?.id]?.[5] || 3, // Default to 3 for Playbook as none not allowed for Playbook
            },
          ].filter((permission) => permission.privilegeId !== null),
        },
      ];
    } else {
      payload = [
        {
          granteeId: rowInEditMode?.id,
          isGroup: rowInEditMode?.isGroup,
          roleId: roleId,
        },
      ];
    }

    try {
      const userAccessChangeResponse = await FetchData({
        requestType: 'withData',
        method: 'POST',
        url: `workspace/user-access?workspaceId=${parentId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        data: payload,
      });

      if (userAccessChangeResponse.status) {
        setAddedUser(null);
        mutateAccessList();
        setOriginalAccessList(JSON.parse(JSON.stringify(accessList)));
        removeEditModeRow();
        setAlert({
          message: 'Changes saved successfully',
          severity: 'success',
        });
      }
    } catch (error) {
      setAlert({
        message: 'Error saving changes. Try Again',
        severity: 'error',
      });
    }
  };

  const handleCancelChanges = () => {
    if (!rowInEditMode?.id) return;

    if (originalAccessList.length !== accessList.length) {
      setAddedUser(null);
      mutateAccessList(
        [...accessList].filter((access) => access.id !== rowInEditMode.id),
        false,
      );
    } else {
      const originalAccess = originalAccessList.find((access) => access.id === rowInEditMode.id);
      if (originalAccess) {
        setAddedUser(null);
        mutateAccessList(
          accessList.map((access) => (access.id === rowInEditMode.id ? originalAccess : access)),
          false,
        );
        setCustomPermissions((prev: any) => {
          const newPermissions = { ...prev };
          if (originalAccess.customPermissions) {
            newPermissions[rowInEditMode.id] = {};
            originalAccess.customPermissions.forEach((permission: any) => {
              newPermissions[rowInEditMode.id][permission.contentTypeId] = permission.privilegeId;
            });
          } else {
            delete newPermissions[rowInEditMode.id];
          }
          return newPermissions;
        });
      }
    }
    removeEditModeRow();
  };

  const handleEditRowButtonClick = (rowId: string) => {
    if (rowInEditMode && rowInEditMode.id === rowId) {
      setAlert({
        message: 'This row is already being edited.',
        severity: 'warning',
      });
      return;
    }

    if (rowInEditMode) {
      setAlert({
        message: 'There are unsaved changes. Can not proceed editing this row.',
        severity: 'error',
      });
      return;
    }

    setRowInEditMode({
      id: rowId,
      name: accessList.find((access) => access.id === rowId)?.name,
      isGroup: accessList.find((access) => access.id === rowId)?.isGroup,
      emailId: accessList.find((access) => access.id === rowId)?.emailId,
      role: accessList.find((access) => access.id === rowId)?.role,
      customPermissions: accessList.find((access) => access.id === rowId)?.customPermissions,
    });
  };

  const isCurrentRowInEditMode = (rowId: string) => {
    return rowInEditMode?.id === rowId;
  };

  const removeEditModeRow = () => {
    setRowInEditMode(null);
  };

  const handlePermissionChange = (userGroupId: any, contentTypeId: any, privilegeId: any) => {
    // Update customPermissions
    setCustomPermissions((prev: any) => ({
      ...prev,
      [userGroupId]: {
        ...prev[userGroupId],
        [contentTypeId]:
          privilegeId !== 0 && privilegeId !== null ? Math.min(Math.max(privilegeId, 1), 3) : null, // Ensure privilegeId is between 1 and 3, or null
      },
    }));

    // Mark user as modified for the blue icon (if user role is "custom_creator")
    // Create a new accessList and update the respective user's customPermissions
    const updatedAccessList = accessList.map((access: any) => {
      if (access.id === userGroupId) {
        // Create or update the customPermissions for the current user
        const updatedPermissions = [
          {
            contentTypeId: 2,
            privilegeId: customPermissions[userGroupId]?.[2] || null,
          },
          {
            contentTypeId: 3,
            privilegeId: customPermissions[userGroupId]?.[3] || null,
          },
          {
            contentTypeId: 4,
            privilegeId: customPermissions[userGroupId]?.[4] || null,
          },
          {
            contentTypeId: 5,
            privilegeId: customPermissions[userGroupId]?.[5] || 3, // Default to 3 for Playbook as none not allowed for Playbook
          },
        ].filter((permission) => permission.privilegeId !== null);
        return { ...access, customPermissions: updatedPermissions };
      }
      return access;
    });
    mutateAccessList(updatedAccessList, false);
  };

  const isAccessRoleDisabled = (id: number) => {
    if (id === 3) {
      return disableOwnerAccess; // returning whether owner role can be assigned
    } else if (id == 4) {
      return disableModeratorAccess; // returning whether moderator role can be
    } else {
      return false;
    }
  };

  const isAccessRoleGreater = (role: string) => {
    // checking if role of the user is of higher priority and accordingly disabling that user's role modification
    const roleId = roles.find((roles) => roles.value === role)?.id;
    if (roleId && roleId <= currentRoleID) {
      return true;
    } else {
      return false;
    }
  };

  const handleRoleChange = (userGroupId: any, newRole: any) => {
    if (!isCurrentRowInEditMode(userGroupId)) return;

    setRowInEditMode((prevRowInEditMode) => {
      if (prevRowInEditMode && prevRowInEditMode.id === userGroupId) {
        return {
          ...prevRowInEditMode,
          role: newRole,
        };
      }
      return prevRowInEditMode;
    });
  };

  const handleDelete = (userGroupId: any, userName: any, isGroup: any, extraParam: any) => {
    setShowUserDeleteConfirmation(true);
    setUser_GroupID(userGroupId);
    setUser_GroupName(userName);
    setIsInGroup(isGroup);
  };

  return (
    <>
      {alert && (
        <NotificationDialog
          onCloseAlert={() => setAlert(null)}
          severity={alert?.severity}
          testMessage={alert?.message}
          openAlert={!!alert}
        />
      )}
      <div
        style={{
          display: 'flex',
        }}
      >
        <table className={`${styles.resourcesTable} resourcesTable`}>
          <thead>
            <tr>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                rowSpan={3}
              >
                User/Group
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                rowSpan={3}
              >
                Workspace Role
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                colSpan={20}
              >
                Resources
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                rowSpan={3}
              >
                Actions
              </th>
            </tr>
            <tr>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                colSpan={4}
              >
                Sub Folder
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                colSpan={4}
              >
                DB Connection
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                colSpan={4}
              >
                Data Sets
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                colSpan={4}
              >
                Data Files
              </th>
              <th
                style={{
                  color: palette.secondary.contrastText,
                  fontSize: fontSize.large,
                  backgroundColor: palette.primary.grey,
                }}
                colSpan={4}
              >
                Playbook
              </th>
            </tr>
            <tr>
              {Array(5)
                .fill(null)
                .map((_, index) => (
                  <React.Fragment key={index}>
                    <Tooltip title={index === 4 ? 'None is not allowed in Playbook' : 'None'} arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <th
                        style={{
                          color: palette.secondary.contrastText,
                          fontSize: fontSize.large,
                          backgroundColor: palette.primary.grey,
                        }}
                      >
                        <img
                          src="/none_header.png"
                          alt="None"
                          style={{ width: '17px', height: '17px' }}
                        ></img>
                      </th>
                    </Tooltip>
                    <Tooltip title="View" arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <th
                        style={{
                          color: palette.secondary.contrastText,
                          fontSize: fontSize.large,
                          backgroundColor: palette.primary.grey,
                        }}
                      >
                        <img
                          src="/view_header.png"
                          alt="View"
                          style={{ width: '17px', height: '17px' }}
                        ></img>
                      </th>
                    </Tooltip>
                    <Tooltip title="Edit" arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <th
                        style={{
                          color: palette.secondary.contrastText,
                          fontSize: fontSize.large,
                          backgroundColor: palette.primary.grey,
                        }}
                      >
                        <img
                          src="/edit_header.png"
                          alt="Edit"
                          style={{ width: '17px', height: '17px' }}
                        ></img>
                      </th>
                    </Tooltip>
                    <Tooltip title="Create" arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <th
                        style={{
                          color: palette.secondary.contrastText,
                          fontSize: fontSize.large,
                          backgroundColor: palette.primary.grey,
                        }}
                      >
                        <img
                          src="/create_header.png"
                          alt="Create"
                          style={{ width: '17px', height: '17px' }}
                        ></img>
                      </th>
                    </Tooltip>
                  </React.Fragment>
                ))}
            </tr>
          </thead>
          <tbody>
            {sortAccessList(accessList).map((access, rowIndex) => (
              <tr
                onMouseEnter={() => setHoverRowState(rowIndex)}
                onMouseLeave={() => setHoverRowState(null)}
                className={`${styles.tableRow} access-row`}
                style={{
                  border: `${isCurrentRowInEditMode(access.id) ? '2px solid black' : 'none'}`,
                  fontWeight: `${isCurrentRowInEditMode(access.id) ? 'bold' : 'normal'}`,
                  backgroundColor: `${isCurrentRowInEditMode(access.id) ? '#f9f9f9' : ''}`,
                }}
                key={access.id}
              >
                <td
                  style={{
                    color: palette.primary.contrastText,
                    textAlign: 'start',
                    fontSize: fontSize.medium,
                    fontWeight: isCurrentRowInEditMode(access.id) ? 'bold' : 'normal',
                  }}
                >
                  {access.name}
                </td>
                <td>
                  {isAccessRoleGreater(access.role) ? (
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        margin: 0,
                        textAlign: 'center',
                        width: '8.125rem',
                        fontWeight: isCurrentRowInEditMode(access.id) ? 'bold' : 'normal',
                      }}
                    >
                      <Select
                        value={access.id === rowInEditMode?.id ? rowInEditMode?.role : access.role}
                        onChange={(e) => handleRoleChange(access.id, e.target.value)}
                        displayEmpty
                        sx={{
                          fontWeight: `${isCurrentRowInEditMode(access.id) ? 'bold' : 'normal'}`,
                          padding: 0,
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'black !important',
                          },
                          '& .MuiSelect-select.css-1ind0d0-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input':
                            {
                              padding: '8.5px 5px !important',
                            },
                        }}
                      >
                        {roles.map((role, index) => (
                          <MenuItem
                            key={index}
                            value={role.value}
                            className={role.value !== access.role ? styles.disabledOption : ''}
                            sx={{
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(175, 153, 219, 0.3) !important',
                              },
                              '&.Mui-selected:hover': {
                                backgroundColor: 'rgba(175, 153, 219, 0.5) !important',
                              },
                            }}
                          >
                            {role.displayValue}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl
                      fullWidth
                      disabled={!isCurrentRowInEditMode(access.id)}
                      style={{
                        cursor: isCurrentRowInEditMode(access.id) ? 'pointer' : 'not-allowed',
                      }}
                      size="small"
                      sx={{
                        margin: 0,
                        textAlign: 'center',
                        width: '8.125rem',
                        fontWeight: isCurrentRowInEditMode(access.id) ? 'bold' : 'normal',
                      }}
                    >
                      <Select
                        value={access.id === rowInEditMode?.id ? rowInEditMode?.role : access.role}
                        onChange={(e) => handleRoleChange(access.id, e.target.value)}
                        displayEmpty
                        sx={{
                          color: 'black',
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'black !important',
                          },
                          '& .MuiSelect-select.css-1ind0d0-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input':
                            {
                              padding: '8.5px 5px !important',
                            },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: isCurrentRowInEditMode(access.id)
                              ? '2px solid black'
                              : '1px solid transparent',
                          },
                        }}
                      >
                        {roles.map((role, index) => (
                          <MenuItem
                            key={index}
                            value={role.value}
                            className={isAccessRoleDisabled(role.id) ? styles.disabledOption : ''}
                            sx={{
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(175, 153, 219, 0.3) !important',
                              },
                              '&.Mui-selected:hover': {
                                backgroundColor: 'rgba(175, 153, 219, 0.5) !important',
                              },
                            }}
                          >
                            {role.displayValue}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </td>
                {Array(5)
                  .fill(null)
                  .map((_, index) => {
                    let privilegeId = 0;
                    if (index === 4) {
                      privilegeId = customPermissions[access.id]?.[index + 1] || 3;
                    } else {
                      privilegeId = customPermissions[access.id]?.[index + 1] || 0;
                    }
                    return (
                      <React.Fragment key={access.id}>
                        <td colSpan={4}>
                          <StepSlider
                            role={
                              access.id === rowInEditMode?.id ? rowInEditMode?.role : access.role
                            }
                            column={index + 1}
                            isEditActive={access.id === rowInEditMode?.id}
                            initialValue={privilegeId}
                            hideCreate={false}
                            onPermissionChange={(column: any, privilegeId: any) =>
                              handlePermissionChange(access.id, column, privilegeId)
                            }
                          />
                        </td>
                      </React.Fragment>
                    );
                  })}
                <td
                  style={{
                    color: palette.primary.contrastText,
                    textAlign: 'center',
                    fontSize: fontSize.medium,
                    fontWeight: isCurrentRowInEditMode(access.id) ? 'bold' : 'normal',
                  }}
                >
                  {isAccessRoleGreater(access.role) ? (
                    <button className={styles.iconButton} style={{ cursor: 'not-allowed' }}>
                      <Tooltip title="Delete"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px',
                            },
                          },
                        }}
                      >
                        <img
                          src="/delete_white.png"
                          alt="Delete User"
                          style={{
                            width: '17px',
                            height: '17px',
                            pointerEvents: 'none',
                          }}
                        ></img>
                      </Tooltip>
                    </button>
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <button
                        onClick={() => {
                          handleEditRowButtonClick(access.id);
                        }}
                        style={{
                          border: 'none',
                          background: 'none',
                        }}
                      >
                        <Tooltip title="Edit"
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '12px',
                              },
                            },
                          }}
                        >
                          <div className={`${styles.accessEditIcon}`}></div>
                        </Tooltip>
                      </button>
                      <button
                        className={styles.iconButton}
                        onClick={() =>
                          handleDelete(access.id, access.name, access.isGroup, access.roleName)
                        }
                      >
                        <Tooltip title="Delete"
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '12px',
                              },
                            },
                          }}
                        >
                          <div className={`${styles.accessDeleteIcon}`}></div>
                        </Tooltip>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div
          style={{
            paddingTop: '116px',
            marginLeft: '-12px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {sortAccessList(accessList).map((access) => (
            <div
              key={access.id}
              style={{
                height: '52px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '4px',
                width: '80px',
                padding: '8px 4px',
                boxSizing: 'border-box',
              }}
            >
              <Tooltip title="Save Changes"
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <button
                  onClick={() => handleSaveButtonClick()}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: rowInEditMode?.id === access.id ? 'pointer' : 'default',
                    padding: '6px',
                    opacity: rowInEditMode?.id === access.id ? 1 : 0,
                    pointerEvents: rowInEditMode?.id === access.id ? 'auto' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    style={{
                      width: '17px',
                      height: '17px',
                    }}
                    src="/green-tick.png"
                  />
                </button>
              </Tooltip>
              <Tooltip title="Cancel Changes"
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <button
                  onClick={handleCancelChanges}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: rowInEditMode?.id === access.id ? 'pointer' : 'default',
                    padding: '6px',
                    opacity: rowInEditMode?.id === access.id ? 1 : 0,
                    pointerEvents: rowInEditMode?.id === access.id ? 'auto' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    style={{
                      width: '14px',
                      height: '14px',
                    }}
                    src="/close-in-access.png"
                  />
                </button>
              </Tooltip>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default AccessTable;
